import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, FilePlus, X, Download, Trash2, File, Upload } from 'lucide-react';
import api from '../components/api';
import { toast } from 'react-toastify';
import useGlobale from '../components/global';
import Sidebar from '../components/SideBar';
import Header from '../components/Header';
import DemandeurMenu from '../components/DemandeurMenu';

const DocumentPage = () => {
    const { user } = useGlobale();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [formData, setFormData] = useState({
        nom: '',
        fichier: null
    });

    // État pour suivre si le formulaire contient des erreurs
    const [errors, setErrors] = useState({});

    // Set sidebar initial state based on screen size
    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth >= 1024);
        };
        
        // Set initial state
        handleResize();
        
        // Add event listener
        window.addEventListener('resize', handleResize);
        
        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await api.get('demandeur/documents/');
            setDocuments(response.data);
        } catch (error) {
            console.error('Erreur lors de la récupération des documents:', error);
            toast.error('Erreur lors de la récupération de vos documents');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        
        // Effacer l'erreur lorsqu'un champ est modifié
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setFormData({
                ...formData,
                fichier: file,
                nom: formData.nom || file.name.split('.')[0] // Use file name without extension as default document name
            });
            
            // Effacer l'erreur fichier
            if (errors.fichier) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.fichier;
                    return newErrors;
                });
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.nom.trim()) {
            newErrors.nom = "Le nom du document est requis";
        }
        
        if (!formData.fichier) {
            newErrors.fichier = "Le fichier est requis";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }
        
        setUploadLoading(true);
        
        // Create form data for file upload
        const uploadData = new FormData();
        uploadData.append('nom', formData.nom);
        uploadData.append('fichier', formData.fichier);
        uploadData.append('utilisateur', user.id);
        
        try {
            await api.post('demandeur/documents/', uploadData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            toast.success('Document ajouté avec succès');
            
            // Mise à jour de la liste des documents
            fetchDocuments();
            setShowModal(false);
        } catch (error) {
            console.error('Erreur lors de l\'ajout du document:', error);
            
            if (error.response && error.response.data) {
                if (typeof error.response.data === 'object') {
                    setErrors(error.response.data);
                } else {
                    toast.error('Erreur lors de l\'ajout du document');
                }
            } else {
                toast.error('Erreur de connexion au serveur');
            }
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setLoading(true);
        try {
            await api.delete(`demandeur/documents/${id}/`);
            toast.success('Document supprimé avec succès');
            setDeleteConfirmation(null);
            // Mettre à jour la liste des documents
            fetchDocuments();
        } catch (error) {
            console.error('Erreur lors de la suppression du document:', error);
            toast.error('Erreur lors de la suppression du document');
        } finally {
            setLoading(false);
        }
    };

    const openAddModal = () => {
        setSelectedDocument(null);
        setFormData({
            nom: '',
            fichier: null
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setErrors({});
    };

    // Fonction pour obtenir l'extension du fichier (ignore les query params S3)
    const getFileExtension = (filename) => {
        const cleanPath = filename.split('?')[0];
        return cleanPath.split('.').pop().toLowerCase();
    };

    // Fonction pour déterminer l'icône basée sur le type de fichier
    const getFileIcon = (filename) => {
        const ext = getFileExtension(filename);
        switch (ext) {
            case 'pdf':
                return <FileText className="h-8 w-8 text-red-500" />;
            case 'doc':
            case 'docx':
                return <FileText className="h-8 w-8 text-blue-500" />;
            case 'xls':
            case 'xlsx':
                return <FileText className="h-8 w-8 text-green-500" />;
            case 'ppt':
            case 'pptx':
                return <FileText className="h-8 w-8 text-orange-500" />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return <FileText className="h-8 w-8 text-purple-500" />;
            default:
                return <File className="h-8 w-8 text-gray-500" />;
        }
    };

    // Fonction pour formater la taille des fichiers
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Fonction pour formater la date de création
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { 
            day: 'numeric', 
            month: 'long', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleDownload = async (doc) => {
        const url = doc.fichier_url || doc.fichier;
        if (!url) {
            toast.error('Fichier introuvable');
            return;
        }
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            const ext = getFileExtension(url);
            const objectUrl = window.URL.createObjectURL(blob);
            const link = window.document.createElement('a');
            link.href = objectUrl;
            link.download = `${doc.nom}.${ext}`;
            window.document.body.appendChild(link);
            link.click();
            window.document.body.removeChild(link);
            window.URL.revokeObjectURL(objectUrl);
        } catch {
            toast.error('Erreur lors du téléchargement');
        }
    };

    const renderDocumentCards = () => {
        if (documents.length === 0) {
            return (
                <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <div className="mb-6">
                        <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center text-orange-500 mx-auto">
                            <FilePlus size={36} strokeWidth={1.5} />
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">Aucun document</h3>
                    <p className="text-gray-600 mb-6 max-w-md mx-auto">
                        Ajoutez vos documents importants tels que votre CV, lettres de recommandation ou diplômes pour les partager avec les recruteurs.
                    </p>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {documents.map((doc) => {
                    const ext = getFileExtension(doc.fichier);
                    
                    return (
                        <div 
                            key={doc.id} 
                            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition duration-300 transform hover:-translate-y-1 border border-gray-100"
                        >
                            <div className="p-6">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="flex items-center">
                                        <div className="p-3 bg-orange-50 rounded-full mr-4">
                                            {getFileIcon(doc.fichier)}
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-800">{doc.nom}</h3>
                                            <p className="text-sm text-gray-500">
                                                {ext.toUpperCase()} • Ajouté le {formatDate(doc.create_at)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="mt-4 flex flex-wrap justify-between items-center pt-4 border-t border-gray-100">
                                    <button 
                                        onClick={() => handleDownload(doc)}
                                        className="bg-orange-50 hover:bg-orange-100 text-orange-600 p-2 rounded-lg transition-colors duration-200 flex items-center"
                                        title="Télécharger"
                                    >
                                        <Download size={16} className="mr-1" />
                                        <span className="text-sm">Télécharger</span>
                                    </button>
                                    <button 
                                        onClick={() => setDeleteConfirmation(doc.id)}
                                        className="bg-red-50 hover:bg-red-100 text-red-600 p-2 rounded-lg transition-colors duration-200 flex items-center"
                                        title="Supprimer"
                                    >
                                        <Trash2 size={16} className="mr-1" />
                                        <span className="text-sm">Supprimer</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderContent = () => {
        if (loading && documents.length === 0) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-orange-500"></div>
                </div>
            );
        }

        return (
            <>
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-bold text-[#437011] mb-2">
                            Documents
                        </h1>
                        <p className="text-gray-600">Gérez vos documents importants</p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center hover:bg-orange-700 transition mt-4 md:mt-0 w-full md:w-auto justify-center"
                    >
                        <Upload size={18} className="mr-2" />
                        Ajouter un document
                    </button>
                </div>

                {renderDocumentCards()}
            </>
        );
    };

    return (
        <div className="flex min-h-screen bg-[#F4F8E9]">
            <Sidebar
                isOpen={isSidebarOpen}
                menuItems={DemandeurMenu()}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="flex-1 flex flex-col w-full">
                <Header
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                    user={user}
                />
                <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8 pb-20 lg:pb-8 w-full">
                    {renderContent()}
                </main>
            </div>

            {/* Modal pour ajouter un document */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                            <h3 className="text-lg font-semibold text-gray-800">
                                Ajouter un document
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="document-name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Nom du document <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="document-name"
                                        type="text"
                                        name="nom"
                                        value={formData.nom}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border ${errors.nom ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                                        placeholder="Ex: CV, Lettre de motivation, Diplôme"
                                    />
                                    {errors.nom && <p className="mt-1 text-sm text-red-600">{errors.nom}</p>}
                                </div>

                                <div>
                                    <label htmlFor="document-file" className="block text-sm font-medium text-gray-700 mb-2">
                                        Fichier <span className="text-red-500">*</span>
                                    </label>
                                    <label 
                                        htmlFor="document-file" 
                                        className={`w-full px-4 py-6 border ${errors.fichier ? 'border-red-500 border-dashed' : 'border-gray-300 border-dashed'} rounded-lg bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors`}
                                    >
                                        <Upload className="h-10 w-10 text-orange-400 mb-2" />
                                        <p className="text-sm text-gray-600 mb-1">Cliquez pour sélectionner un fichier</p>
                                        <p className="text-xs text-gray-500">PDF, Word, Excel, PowerPoint, images</p>
                                        <input
                                            id="document-file"
                                            type="file"
                                            name="fichier"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </label>
                                    {formData.fichier && (
                                        <div className="mt-2 flex items-center">
                                            <File className="h-5 w-5 text-orange-500 mr-2" />
                                            <p className="text-sm text-gray-600">{formData.fichier.name}</p>
                                        </div>
                                    )}
                                    {errors.fichier && <p className="mt-1 text-sm text-red-600">{errors.fichier}</p>}
                                </div>
                            </div>

                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploadLoading}
                                    className={`px-4 py-2 rounded-lg text-white ${uploadLoading ? 'bg-orange-400' : 'bg-orange-600 hover:bg-orange-700'}`}
                                >
                                    {uploadLoading ? (
                                        <span className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Téléchargement...
                                        </span>
                                    ) : "Ajouter"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de confirmation de suppression */}
            {deleteConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-4">Confirmer la suppression</h3>
                            <p className="text-gray-600 mb-6">
                                Êtes-vous sûr de vouloir supprimer ce document ? Cette action est irréversible.
                            </p>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={() => setDeleteConfirmation(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={() => handleDelete(deleteConfirmation)}
                                    className="px-4 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700"
                                >
                                    Supprimer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentPage;
